import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";

/**
 * UploadSelection - Step 2: Choose upload method (images or PDF)
 * Opens native file picker on click, passes selected file to parent.
 *
 * @param {Function} onFileSelected - Called with the selected File object
 */
const UploadSelection = ({ onFileSelected }) => {
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    // Reset so same file can be re-selected
    e.target.value = "";
  };

  const handlePdfChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    e.target.value = "";
  };

  return (
    <Box>
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={handleImageChange}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={handlePdfChange}
      />

      {/* Title */}
      <Typography
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 0.5,
        }}
      >
        how would you like to add your menu?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "#6b7280",
          textTransform: "lowercase",
          mb: 3,
        }}
      >
        choose a method to digitize your restaurant's menu items and pricing.
      </Typography>

      {/* Upload options */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
          mb: 4,
        }}
      >
        {/* Upload Images */}
        <Box
          onClick={() => imageInputRef.current?.click()}
          sx={{
            flex: 1,
            border: "2px dashed #d1d5db",
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#7C3AED",
              bgcolor: "#F3EEFF",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(124, 58, 237, 0.1)",
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "#F3EEFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <ImageOutlinedIcon sx={{ fontSize: 24, color: "#7C3AED" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.25,
            }}
          >
            upload images
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#9ca3af",
              textTransform: "lowercase",
            }}
          >
            from your photo library
          </Typography>
        </Box>

        {/* Upload PDF */}
        <Box
          onClick={() => pdfInputRef.current?.click()}
          sx={{
            flex: 1,
            border: "2px dashed #d1d5db",
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#7C3AED",
              bgcolor: "#F3EEFF",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 16px rgba(124, 58, 237, 0.1)",
            },
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "#F3EEFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 24, color: "#7C3AED" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 700,
              color: "#111827",
              textTransform: "lowercase",
              mb: 0.25,
            }}
          >
            upload pdf
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#9ca3af",
              textTransform: "lowercase",
            }}
          >
            from your files or drive
          </Typography>
        </Box>
      </Box>

      {/* Tips section */}
      <Typography
        sx={{
          fontSize: "0.95rem",
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 1.5,
        }}
      >
        tips for best results
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        {/* Tip 1: Readable text */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#f9fafb",
            borderRadius: 2.5,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <MenuBookOutlinedIcon
              sx={{ fontSize: 22, color: "#7C3AED", mt: 0.25 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "#111827",
                  textTransform: "lowercase",
                  mb: 0.5,
                }}
              >
                ensure text is readable
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "#9ca3af",
                  textTransform: "lowercase",
                  lineHeight: 1.5,
                }}
              >
                avoid blurry shots. make sure every item and price is clear.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tip 2: Avoid glare */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#f9fafb",
            borderRadius: 2.5,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <WbSunnyOutlinedIcon
              sx={{ fontSize: 22, color: "#7C3AED", mt: 0.25 }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: "#111827",
                  textTransform: "lowercase",
                  mb: 0.5,
                }}
              >
                avoid glare
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "#9ca3af",
                  textTransform: "lowercase",
                  lineHeight: 1.5,
                }}
              >
                direct light can cause reflections on glossy menus. use natural
                light.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UploadSelection;
